import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";

import { actionGroup } from "actions/editor";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";

import _ from "lodash";
import cx from "classnames";

import PageDetailCard from "./pageDetailsCard";
import { HeaderContext } from "pages/pages/components/header";
import StoryboardContext from "components/Storyboard/context";
import { EditorContext } from "components/Editor";
import { createPage, rearrangePageWidgets, searchPages, updatePage } from "actions/pages";
import { getBaseUrl } from "services/util";
import { BackgroundView } from "../../background";
import { colorToString } from "components/Editor/helper/color";
import { Property } from "components/Editor/components/properties";
import { Button, Accordion } from "react-bootstrap";

import styles from "./components.module.scss";

export const SlidePageDetailsComponent = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const listReference = React.createRef(null);
  const headerContext = React.useContext(HeaderContext);
  const history = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();
  const background = useSelector((state) =>
    _.get(state, "storyboards.storyboard.item.appearance.background")
  );
  const loading = useSelector(state => _.get(state, 'pages.loading', false));
  const fetched = useSelector(state => _.get(state, 'pages.fetched', false));
  const pagesList = useSelector((state) => _.get(state, `pages.items`, []));
  const creating = useSelector(state => _.get(state, 'pages.creating', false));

  const [items, setItems] = useState(pagesList);

  useEffect(() => {
    if (!_.isEqual(items, pagesList)) {
      setItems(pagesList);
    }
  }, [pagesList]);

  const storyboardContext = React.useContext(StoryboardContext);
  const editorContext = React.useContext(EditorContext);


  const handleDragEnd = (event) => {
    const selecto = editorContext.editorSession.selecto.current;
    const selectoElement = document.querySelector(`.${selecto.selectionElement.classList[0]}`);
    selectoElement.setAttribute("style", "display: none;");

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => `draggable-${item.id}` === active.id);
      const newIndex = items.findIndex((item) => `draggable-${item.id}` === over.id);
      const orderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({ ...item, order: index }));
      setItems(orderedItems);

      const newItems = orderedItems.map(item => _.pick(item, ["id", "order"]));
      let request = {
        entityOrderList: newItems
      }
      dispatch(rearrangePageWidgets(id, request, orderedItems));
    }
  };

  const handleAddPageClick = () => {
    if (!creating) {
      var request = {
        name: `Page ${_.size(pagesList) + 1}`,
        storyboard: storyboardContext.id,
        order: _.last(pagesList).order + 1
      };
      actionGroup.start();
      // dispatch(updatePage(page.id, editorSession.current));
      dispatch(
        createPage(request, (response) => {
          history.push(`${baseUrl}/pages/${response.id}`);
        })
      );
      actionGroup.end();
    }
  };

  useEffect(() => {
    if (storyboardContext && !fetched) {
      handleSearchPages();
    }
  }, [storyboardContext]);

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboardContext.id
    }

    dispatch(searchPages(request, 0, 50));
  }

  return (
    <div
      className={cx(styles.pageDetailsContainer, {
        [styles.showPageDetails]: show,
      })}
    >
      <div className={styles.overlay} />
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <span className={styles.title}>Pages & Slides</span>
            <Button
              variant="info"
              onClick={onClose}
              className={styles.closeButton}
            >
              <i className={cx(styles.icon, "cleverstory-close")}></i>
            </Button>
          </div>
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((widget) => `draggable-${widget.id}`)}>
              <div className={styles.actionContainer}>
                <div className={styles.inner} ref={listReference}>
                  <Accordion className={styles.accordWrapper} defaultActiveKey={id}>
                    {_.map(items, (page) => {
                      return (
                        <PageDetailCard
                          key={page.id}
                          id={`draggable-${page.id}`}
                          eventKey={page.id}
                          page={page}
                          currentPage={id}
                        />
                      );
                    })}
                  </Accordion>
               </div>
              </div>
            </SortableContext>
          </DndContext>
          <div className={styles.addPageButtonContainer}>
            <Button
              onClick={handleAddPageClick}
              className={styles.addPageButton}
              variant="primary"
            >
              <i className={cx("cleverstory-plus", styles.icon)} />
              Add Page
            </Button>
          </div>
        </div>
      </div>
  );
};

export const GlassThemesSettings = React.memo(
  ({ background, handleBackgroundChange, handleCustomBlur }) => {
    const { type, color, blur } = background || {type: 'glass', color:{ r: 238, g: 238, b: 238, a: 0.25 }, blur: 25 };
    
    const [custom, setCustom] = useState(false);

    const handleChangeBackgroundColor = (color) => {
      handleBackgroundChange({
        ...background,
        type: "glass",
        blur: 25,
        color: {
          ...color,
          a: 0.25,
        },
        media: null,
      });
    };

    useEffect(() => {
      if (!_.isEqual(_.omit(color, ['type']), { r: 98, g: 98, b: 98, a: 0.25 }) && !_.isEqual(_.omit(color, ['type']), { r: 238, g: 238, b: 238, a: 0.25 })) {
        setCustom(true);
      }

      return () => {
        setCustom(false);
      };
    }, [background]);

    return (
      <>
        <Property name="Glass Effect">
          <div className={styles.glassThemeContainer}>
            <div
              className={styles.themeButton}
              onClick={() => {
                handleChangeBackgroundColor({ r: 98, g: 98, b: 98, a: 0.25 });
                setCustom(false);
              }}
            >
              <div
                className={cx(styles.darkBox, {
                  [styles.active]:
                    type == "glass" &&
                    _.isEqual(_.omit(color, ['type']), { r: 98, g: 98, b: 98, a: 0.25 }),
                })}
              >
                <span className={styles.swatch} />
              </div>
              <span className={styles.title}>Dark</span>
            </div>
            <div
              className={styles.themeButton}
              onClick={() => {
                handleChangeBackgroundColor({
                  r: 238,
                  g: 238,
                  b: 238,
                  a: 0.25,
                });
                setCustom(false);
              }}
            >
              <div
                className={cx(styles.lightBox, {
                  [styles.active]:
                    type == "glass" &&
                    _.isEqual(_.omit(color, ['type']), { r: 238, g: 238, b: 238, a: 0.25 }),
                })}
              >
                <span className={styles.swatch} />
              </div>
              <span className={styles.title}>Light</span>
            </div>
            <Property.ColorButton
              trigger={
                <div className={styles.themeButton}>
                  <div
                    className={cx(styles.customBox, {
                      [styles.active]:
                        type == "glass" &&
                        !_.isEqual(_.omit(color, ['type']), { r: 98, g: 98, b: 98, a: 0.25 }) &&
                        !_.isEqual(_.omit(color, ['type']), { r: 238, g: 238, b: 238, a: 0.25 }),
                    })}
                  >
                    <span
                      style={{
                        backgroundColor:
                          type == "glass" &&
                          !_.isEqual(_.omit(color, ['type']), { r: 98, g: 98, b: 98, a: 0.25 }) &&
                          !_.isEqual(_.omit(color, ['type']), { r: 238, g: 238, b: 238, a: 0.25 })
                            ? colorToString(color)
                            : "#FFC600",
                      }}
                      className={styles.swatch}
                    />
                  </div>
                  <span className={styles.title}>Custom</span>
                </div>
              }
              value={color}
              onChange={(color) => {
                handleChangeBackgroundColor(color);
              }}
            />
          </div>
        </Property>
        {custom && (
          <Property name="Background Blur">
            <Property.Slider
              input
              min={5}
              className={styles.customSlider}
              max={100}
              step={1}
              value={blur || 0}
              onChange={handleCustomBlur}
            />
          </Property>
        )}
      </>
    );
  }
);
